import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import { LocalesContext } from "../../contexts/LocalesContext";
import InputSelectComponent from "../../components/InputSelectComponent";
import InputNumberComponent from "../../components/InputNumberComponent";
import InputSwitchComponent from "../../components/InputSwitchComponent";
import ButtonComponent from "../../components/ButtonComponent";
import ModalComponent from "../../components/ModalComponent";
import RowsPresetComponent from "../../components/RowsPresetComponent";
import InputLabelButtonComponent from "../../components/InputLabelButtonComponent";
import { MachineContext } from "../../contexts/MachineContext";
import { postRequest } from "../../utils/requests";
import { objValueOrDefault } from "../../utils/data";
import {
  presetSensibilitaGraficoOptions,
  presetGraficoDiDefaultOptions,
} from "../../utils/miplus";

export default function EditorScreen(props) {
  const isPresetEdit = props.type == "preset_edit";

  const { t } = useContext(LocalesContext);
  const [formValues, setFormValues] = useState({
    setupFile: objValueOrDefault(props, ["params", "setupFile"], []),
    interfila: objValueOrDefault(props, ["params", "interfila"], 45),
    distanzaSemina: objValueOrDefault(props, ["params", "distanzaSemina"], 20),
    investimento: objValueOrDefault(props, ["params", "investimento"], 111.1),
    numeroForiDisco: objValueOrDefault(
      props,
      ["params", "numeroForiDisco"],
      24
    ),
    diametroForiDisco: objValueOrDefault(
      props,
      ["params", "diametroForiDisco"],
      5
    ),
    graficoDefault: objValueOrDefault(
      props,
      ["params", "graficoDefault"],
      "population"
    ),
    sensibilitaGrafico: objValueOrDefault(
      props,
      ["params", "sensibilitaGrafico"],
      3
    ),
    sogliaAllarmeQualita: objValueOrDefault(
      props,
      ["params", "sogliaAllarmeQualita"],
      20
    ),
    sogliaAllarmePopolazione: objValueOrDefault(
      props,
      ["params", "sogliaAllarmePopolazione"],
      20
    ),
    livelloAllarmeGrafico: objValueOrDefault(
      props,
      ["params", "livelloAllarmeGrafico"],
      80
    ),
    livelloDepressioneMin: objValueOrDefault(
      props,
      ["params", "livelloDepressioneMin"],
      35
    ),
    livelloDepressioneMax: objValueOrDefault(
      props,
      ["params", "livelloDepressioneMax"],
      50
    ),
    ripristinoAutoFile: objValueOrDefault(
      props,
      ["params", "ripristinoAutoFile"],
      false
    ),
  });
  const [editEndModal, setEditEndModal] = useState(false);
  const { machineParams } = useContext(MachineContext);

  let title = isPresetEdit ? t("application.titles.presets_edit") : t("application.titles.presets_new");

  // FUNCTIONS
  /////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Funzione eseguita alla conferma di completamento dell'editor
   */
  const onConfirm = () => {
    props.goTo("presetsConfirm", {
      type: props.type,
      preset: props.preset
        ? Object.assign(props.preset, { params: formValues })
        : { params: formValues },
    });
  };

  /**
   * Funzione eseguita quando l'utente torna indietro.
   */
  const onGoBack = () => {
    props.goBack();
  };

  /**
   * Funzione eseguita per aggiornare un valore del form
   * @param {*} name
   * @param {*} value
   */
  const updateFormValue = (name, value) => {
    const newFormValues = Object.assign({}, formValues);
    newFormValues[name] = value;

    if (name === "interfila") {
      newFormValues.investimento =
        Math.round((10 * 100000) / (value * newFormValues.distanzaSemina)) / 10;
    }
    if (name === "distanzaSemina") {
      newFormValues.investimento =
        Math.round((10 * 100000) / (value * newFormValues.interfila)) / 10;
    }
    if (name === "investimento") {
      newFormValues.distanzaSemina =
        Math.round((10 * 100000) / (value * newFormValues.interfila)) / 10;
    }
    if (name === "livelloDepressioneMin") {
      newFormValues.livelloDepressioneMax =
        newFormValues.livelloDepressioneMax || 50;
      if (newFormValues.livelloDepressioneMax < value) {
        newFormValues.livelloDepressioneMax = value;
      }
    }

    if (name == "sensibilitaGrafico") {
      if (value == 2) {
        newFormValues.sogliaAllarmeQualita = 60;
        newFormValues.sogliaAllarmePopolazione = 30;
        newFormValues.livelloAllarmeGrafico = 80;
      }
      if (value == 3) {
        newFormValues.sogliaAllarmeQualita = 20;
        newFormValues.sogliaAllarmePopolazione = 20;
        newFormValues.livelloAllarmeGrafico = 80;
      }
      if (value == 4) {
        newFormValues.sogliaAllarmeQualita = 10;
        newFormValues.sogliaAllarmePopolazione = 10;
        newFormValues.livelloAllarmeGrafico = 80;
      }
    }

    setFormValues(newFormValues);
  };

  /**
   * Funzione eseguita alla richiesta di rimozione del preset
   */
  const onRemove = () => {
    postRequest(
      props.apis.presets_manage_actions_action,
      { actions: [{ uuid: props.preset.uuid, type: "remove" }] },
      (response) => {
        if (response.result) {
          props.goTo("presets");
        }
      }
    );
  };

  // RENDER
  /////////////////////////////////////////////////////////////////////////////////////////////

  return (
    <div className="c-form">
        <div className="c-form__body">
          <button className="c-form__close" onClick={onGoBack}>
            <i className="fas fa-times"></i>
          </button>
          
          <div className="c-form__header">
            <div className="c-input__title">{title}</div>
          </div>

          <div>
            <InputLabelButtonComponent
              label={t("NumeroFile")}
              labelValue={(
                formValues.setupFile.length || machineParams.setup?.numeroFile || 24
              ).toString()}
              btnCta={t("application.cta.edit")}
              btnOnClick={() => setEditEndModal(true)}
            />

            <InputNumberComponent
              label={t("Interfila")}
              onChange={(v) => updateFormValue("interfila", v)}
              value={formValues.interfila}
              unit="cm"
              min={25}
              max={200}
            />
            
            <InputNumberComponent
              label={t("DistanzaSemina")}
              onChange={(v) => updateFormValue("distanzaSemina", v)}
              value={formValues.distanzaSemina}
              unit="cm"
              min={1}
              max={200}
              decimal={1}
            />
            
            <InputNumberComponent
              label={t("Investimento")}
              onChange={(v) => updateFormValue("investimento", v)}
              value={formValues.investimento}
              unit="kseed/ha"
              min={1}
              max={5000}
              decimal={1}
            />
            
            <InputNumberComponent
              label={t("NumeroForiDisco")}
              onChange={(v) => updateFormValue("numeroForiDisco", v)}
              value={formValues.numeroForiDisco}
              unit="n°"
              min={1}
              max={250}
            />
            
            <InputNumberComponent
              label={t("DiametroForiDisco")}
              onChange={(v) => updateFormValue("diametroForiDisco", v)}
              value={formValues.diametroForiDisco}
              unit="mm"
              min={1}
              max={10}
              decimal={1}
            />
            
            <InputSelectComponent
              label={t("GraficoDiDefault")}
              options={presetGraficoDiDefaultOptions(t)}
              onChange={(v) => updateFormValue("graficoDefault", v)}
              value={formValues.graficoDefault}
            />
            
            <InputSelectComponent
              label={t("SensibilitaGrafico")}
              options={presetSensibilitaGraficoOptions(t)}
              onChange={(v) => updateFormValue("sensibilitaGrafico", v)}
              value={formValues.sensibilitaGrafico}
            />
            
            <InputNumberComponent
              label={t("SogliaAllarmeQualita")}
              onChange={(v) => updateFormValue("sogliaAllarmeQualita", v)}
              value={formValues.sogliaAllarmeQualita}
              unit="%"
              min={1}
              max={100}
              disabled={formValues.sensibilitaGrafico != 1}
            />
            
            <InputNumberComponent
              label={t("SogliaAllarmePopolazione")}
              onChange={(v) => updateFormValue("sogliaAllarmePopolazione", v)}
              value={formValues.sogliaAllarmePopolazione}
              unit="%"
              min={1}
              max={100}
              disabled={formValues.sensibilitaGrafico != 1}
            />
            
            <InputNumberComponent
              label={t("LivelloAllarmeGrafico")}
              onChange={(v) => updateFormValue("livelloAllarmeGrafico", v)}
              value={formValues.livelloAllarmeGrafico}
              unit="%"
              min={1}
              max={100}
              disabled={formValues.sensibilitaGrafico != 1}
            />
            
            <InputNumberComponent
              label={t("LivelloDepressioneMin")}
              onChange={(v) => updateFormValue("livelloDepressioneMin", v)}
              value={formValues.livelloDepressioneMin}
              unit="mbar"
              min={10}
              max={70}
            />
            
            <InputNumberComponent
              label={t("LivelloDepressioneMax")}
              onChange={(v) => updateFormValue("livelloDepressioneMax", v)}
              value={formValues.livelloDepressioneMax}
              unit="mbar"
              min={formValues.livelloDepressioneMin || 10}
              max={70}
            />
            
            <InputSwitchComponent
              label={t("RipristinoAutoFile")}
              onChange={(v) => updateFormValue("ripristinoAutoFile", v)}
              value={formValues.ripristinoAutoFile}
              val1={false}
              val2={true}
              lab1={t("application.labels.not_active")}
              lab2={t("application.labels.active")}
            />
          </div>

          <div className="c-form__actions">
            <div className="col-3">
              <div className="row row--buttons">
                <ButtonComponent
                  className="c-button c-button--cenere"
                  label={t("application.cta.cancel")}
                  confirm={t("application.messages.confirm_cancel")}
                  onClick={() => props.goBack()}
                  warning
                />
                <ButtonComponent
                  className="c-button"
                  label={t("application.cta.continue")}
                  disabled={false}
                  onClick={onConfirm}
                />
                {isPresetEdit && (
                  <ButtonComponent
                    className="c-button c-button--orange"
                    label={t("application.cta.remove")}
                    confirm={t("application.messages.confirm_remove")}
                    onClick={onRemove}
                    danger
                  />
                )}
              </div>
            </div>
          </div>
  
      </div>

      <ModalComponent
        status={editEndModal}
        onClose={() => setEditEndModal(false)}
        large={true}
      >
        <RowsPresetComponent
          rows={
            machineParams.setup && machineParams.setup.numeroFile
              ? machineParams.setup.numeroFile
              : 24
          }
          rowsActive={formValues.setupFile}
          onRowsChange={(newRows) => updateFormValue("setupFile", newRows)}
        />
      </ModalComponent>
    </div>
  );
}

EditorScreen.propTypes = {
  goBack: PropTypes.func,
};

EditorScreen.defaultProps = {
  goBack: () => {},
};